import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// ─────────────────────────────────────────────────────────────────────────────
// Receivables Breakdown (Pat, Sep 22 2026).
//
// The Billed · Not Paid column of Work Orders To Be Billed, opened out to take the
// whole screen, four columns wide by how long since the bill went out: 0–30, 31–60,
// 61–90 and over 90 days. Each card says how many days since it was sent, how many
// days past net-30, and how long since anyone followed up. From the card you can send
// a follow-up email (one template per column — the office and Executives change the
// wording), log a phone call, post a daily update on the work order, or mark it paid.
// Office, PMs and Executives.
// ─────────────────────────────────────────────────────────────────────────────

const STAGES = [
  { key: 30,  label: '0 – 30 days',   tint: '#0f766e', bg: '#f0fdfa', edge: '#99f6e4', hint: 'Inside terms. Quiet, usually.' },
  { key: 60,  label: '31 – 60 days',  tint: '#b45309', bg: '#fffbeb', edge: '#fde68a', hint: 'Past net 30. A friendly nudge.' },
  { key: 90,  label: '61 – 90 days',  tint: '#c2410c', bg: '#fff7ed', edge: '#fed7aa', hint: 'Past due. Firmer.' },
  { key: 120, label: 'Over 90 days',  tint: '#b91c1c', bg: '#fef2f2', edge: '#fecaca', hint: 'Final notices.' },
];
const money = n => (n == null || n === '' ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const shortDate = s => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const ago = n => (n == null ? 'never' : n === 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`);
const btn = { padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' };
const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const lab = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 3 };
const pill = (bg, fg, edge) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 999, background: bg, color: fg, border: `1px solid ${edge}`, fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap' });

function me() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null') || {}; } catch { return {}; } }

// source: 'wo' = billed work orders (one row per work order); 'large' = sent pay
// applications / invoices on large jobs (one row per invoice, follow-ups per invoice).
export default function ReceivablesBoard({ onClose, source = 'wo' }) {
  const navigate = useNavigate();
  const large = source === 'large';
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [editing, setEditing] = useState(false);
  const session = me();
  const canEditWording = ['admin', 'Admin', 'Executive', 'Accounting'].includes(session.role);

  const load = useCallback(() => {
    apiFetch(large ? '/invoices/receivables' : '/work-orders/receivables').then(d => { setData(d); setErr(''); }).catch(e => setErr(e?.error || e?.message || 'Could not load receivables.'));
  }, [large]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const flash = m => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const byStage = useMemo(() => {
    const out = Object.fromEntries(STAGES.map(s => [s.key, []]));
    for (const r of data?.rows || []) (out[r.stage] || out[120]).push(r);
    return out;
  }, [data]);
  const total = (data?.rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'var(--gray-bg, #f4f4f2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {toast && <div className="toast">{toast}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 800, color: '#111' }}>📊 Receivables Breakdown{large ? ' · Large Jobs' : ''}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Billed · Not Paid, by days since the bill went out · terms net {data?.terms_days || 30}</div>
        </div>
        {data && (
          <div style={{ marginLeft: 8, textAlign: 'right' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111', lineHeight: 1.1 }}>{money(total)}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>outstanding on {data.rows.length} {large ? 'bill' : 'work order'}{data.rows.length === 1 ? '' : 's'}</div>
          </div>
        )}
        <span style={{ flex: 1 }} />
        {data && !data.mail_ready && <span style={pill('#fef2f2', '#b91c1c', '#fecaca')}>Email isn’t set up on the server — follow-ups can be logged, not sent</span>}
        {canEditWording && (
          <button onClick={() => setEditing(true)} style={{ ...btn, background: '#fff', border: '1.5px solid #d1d5db', color: '#374151' }}>✎ Follow-up wording</button>
        )}
        <button onClick={onClose} style={{ ...btn, background: '#111', color: '#fff' }}>✕ Close</button>
      </div>

      {err && <div style={{ padding: '10px 20px', color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {!data && !err && <div style={{ padding: 30, color: '#6b7280' }}>Loading…</div>}

      {data && (
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(270px, 1fr))', gap: 12, alignItems: 'start', minWidth: 1120 }}>
            {STAGES.map(s => {
              const rows = byStage[s.key];
              const sub = rows.reduce((t, r) => t + (Number(r.amount) || 0), 0);
              return (
                <div key={s.key} style={{ background: s.bg, border: `1px solid ${s.edge}`, borderRadius: 12, padding: 10, minHeight: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 4px' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: s.tint }}>{s.label}</span>
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{rows.length}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 800, color: '#111' }}>{money(sub)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', padding: '0 4px 9px' }}>{s.hint}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {rows.map(r => <Card key={r.id} r={r} stage={s} large={large || !!r.per_invoice} mailReady={data.mail_ready} onChanged={load} onFlash={flash} onError={setErr}
                      onOpen={() => navigate((large || r.per_invoice) ? `/admin/jobs/${r.project_id}?pillow=wo_billing` : `/admin/jobs/${r.id}/job_info`)} />)}
                    {rows.length === 0 && <div style={{ fontSize: 11.5, color: '#c4c4c4', padding: '8px 6px' }}>Nothing here.</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing && <WordingEditor onClose={() => setEditing(false)} onSaved={() => { setEditing(false); flash('Wording saved.'); load(); }} />}
    </div>
  );
}

// No sent date on file (it reached this column on the old board): the office types the
// day the bill went, and the card moves to the right column.
function SentDate({ r, onChanged, onError }) {
  const [d, setD] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!d || busy) return; setBusy(true); onError('');
    try { await apiFetch(`/work-orders/${r.id}/bill/sent-date`, { method: 'POST', body: JSON.stringify({ date: d }) }); onChanged(); }
    catch (e) { onError(e?.error || e?.message || 'That did not save.'); }
    setBusy(false);
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
      <span style={pill('#fffbeb', '#b45309', '#fde68a')}>⚠ no sent date</span>
      <span style={{ fontSize: 11, color: '#6b7280' }}>Sent on</span>
      <input type="date" value={d} max={new Date().toLocaleDateString('en-CA')} onChange={e => setD(e.target.value)} style={{ ...inp, width: 'auto', padding: '4px 6px', fontSize: 12 }} />
      <button onClick={save} disabled={!d || busy} style={{ ...btn, padding: '5px 10px', background: d ? '#b45309' : '#e5e7eb', color: d ? '#fff' : '#9ca3af' }}>{busy ? '…' : 'Save'}</button>
    </div>
  );
}

// A row with per_invoice is one invoice on a WORK ORDER (its second or third bill) —
// handled exactly like a large-job invoice row (Ryan, Sep 30 2026).
function Card({ r, stage, large = false, mailReady, onChanged, onFlash, onError, onOpen }) {
  // A large-job row is one invoice on a job; the follow-up and the update are filed on the job.
  const jobId = large ? r.project_id : r.id;
  const followupBody = extra => JSON.stringify(large ? { ...extra, invoice_id: r.id } : extra);
  const [mode, setMode] = useState(null);      // 'email' | 'call' | 'update' | null
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState('');
  const overdue = r.days_overdue > 0;
  const stale = r.days_since_sent > 30 && (r.days_since_followup == null || r.days_since_followup > 14);

  const openEmail = () => { setDraft({ to: r.draft.to, cc: r.draft.cc, subject: r.draft.subject, body: r.draft.body, attach: true }); setMode('email'); };
  async function sendEmail() {
    if (busy) return; setBusy(true); onError('');
    try {
      const out = await apiFetch(`/work-orders/${jobId}/followup`, { method: 'POST', body: followupBody({ kind: 'email', ...draft }) });
      onFlash(`Follow-up sent to ${out.to}${out.attached ? ' with the invoice attached' : ''}.`);
      setMode(null); onChanged();
    } catch (e) { onError(e?.error || e?.message || 'That did not send.'); }
    setBusy(false);
  }
  async function logCall() {
    if (busy) return; setBusy(true); onError('');
    try {
      await apiFetch(`/work-orders/${jobId}/followup`, { method: 'POST', body: followupBody({ kind: 'call', note }) });
      onFlash('Call logged.'); setMode(null); setNote(''); onChanged();
    } catch (e) { onError(e?.error || e?.message || 'That did not save.'); }
    setBusy(false);
  }
  async function postUpdate() {
    if (busy || !note.trim()) return; setBusy(true); onError('');
    try {
      await apiFetch(`/projects/${jobId}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: note.trim(), board_scope: 'receivables' }) });
      onFlash('Update posted on the work order.'); setMode(null); setNote(''); onChanged();
    } catch (e) { onError(e?.error || e?.message || 'That did not post.'); }
    setBusy(false);
  }
  async function markPaid() {
    if (busy || !window.confirm(`Mark ${r.project_number}${large && r.wo_bill_invoice_no ? ' invoice ' + r.wo_bill_invoice_no : ''} paid?`)) return; setBusy(true); onError('');
    try { await apiFetch(large ? `/invoices/${r.id}/paid` : `/work-orders/${r.id}/paid`, { method: 'POST' }); onFlash(`${r.project_number} marked paid.`); onChanged(); }
    catch (e) { onError(e?.error || e?.message || 'That did not go through.'); }
    setBusy(false);
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 11px', opacity: busy ? 0.6 : 1 }}>
      <button onClick={onOpen} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#c2410c' }}>{r.project_number}</div>
        <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.35 }}>{r.name}</div>
      </button>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 3 }}>{r.contact?.company || r.client_name || '—'}{r.contact?.name ? ` · ${r.contact.name}` : ''}{r.pm_name ? ` · PM ${r.pm_name}` : ''}</div>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: '#111', marginTop: 5 }}>
        {money(r.amount)}
        {(r.wo_bill_invoice_no || r.invoice?.invoice_no) && <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}> · inv {r.wo_bill_invoice_no || r.invoice.invoice_no}{large && r.application_no ? ` · pay app #${r.application_no}` : ''}</span>}
      </div>
      {large && r.retainage_held != null && Number(r.retainage_held) > 0 && <div style={{ fontSize: 10.5, color: '#6b7280' }}>retainage held {money(r.retainage_held)}</div>}
      {r.wo_bill_sent_at
        ? <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 2 }}>Sent {shortDate(r.wo_bill_sent_at)}{r.wo_bill_sent_by_name ? ` by ${r.wo_bill_sent_by_name}` : ''}</div>
        : <SentDate r={r} onChanged={onChanged} onError={onError} />}

      {/* The three pillows: how long out, how far past terms, how long since a follow-up. */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
        <span style={pill('#f3f4f6', '#374151', '#e5e7eb')}>📤 {r.days_since_sent} day{r.days_since_sent === 1 ? '' : 's'} out</span>
        <span style={overdue ? pill('#fef2f2', '#b91c1c', '#fecaca') : pill('#f0fdf4', '#15803d', '#bbf7d0')}>{overdue ? `⏰ ${r.days_overdue} day${r.days_overdue === 1 ? '' : 's'} overdue` : '✓ inside terms'}</span>
        <span style={stale ? pill('#fffbeb', '#b45309', '#fde68a') : pill('#f3f4f6', '#374151', '#e5e7eb')} title={r.last_followup ? `${r.last_followup.kind === 'call' ? 'Call' : 'Email'} by ${r.last_followup.by_name || 'someone'}${r.last_followup.note ? ' — ' + r.last_followup.note : ''}` : 'No follow-up sent or logged yet'}>
          {r.last_followup ? `${r.last_followup.kind === 'call' ? '📞' : '✉️'} follow-up ${ago(r.days_since_followup)}` : '📣 no follow-up yet'}{r.followup_count > 1 ? ` · ${r.followup_count}×` : ''}
        </span>
      </div>

      {r.last_update && (
        <div style={{ marginTop: 8, borderLeft: '3px solid #e5e7eb', paddingLeft: 8, fontSize: 12, color: '#374151' }}>
          <div style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 700 }}>{r.last_update.author_name || 'Someone'} · {shortDate(r.last_update.created_at)}</div>
          <div style={{ lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{String(r.last_update.notes || '').slice(0, 220)}{String(r.last_update.notes || '').length > 220 ? '…' : ''}</div>
        </div>
      )}

      {mode === null && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10 }}>
          <button onClick={openEmail} disabled={!mailReady} title={mailReady ? `Send the ${stage.key}-day follow-up email, invoice attached` : 'Email is not set up on the server yet'}
            style={{ ...btn, background: mailReady ? stage.tint : '#e5e7eb', color: mailReady ? '#fff' : '#9ca3af' }}>✉️ Send follow-up</button>
          <button onClick={() => setMode('call')} style={{ ...btn, background: '#fff', border: `1.5px solid ${stage.tint}`, color: stage.tint }}>📞 Log a call</button>
          <button onClick={() => setMode('update')} style={{ ...btn, background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1d4ed8' }}>➕ Daily update</button>
          <button onClick={markPaid} style={{ ...btn, background: '#166534', color: '#fff' }}>Mark paid</button>
        </div>
      )}

      {mode === 'email' && draft && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: stage.tint }}>{stage.key}-day follow-up{r.attachment ? ` · attaches ${r.attachment}` : ' · no invoice PDF on file to attach'}</div>
          <label><span style={lab}>To</span><input style={inp} value={draft.to} onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} placeholder="billing@customer.com" /></label>
          <label><span style={lab}>Cc <span style={{ fontWeight: 500, textTransform: 'none' }}>(PM and the office)</span></span><input style={inp} value={draft.cc} onChange={e => setDraft(d => ({ ...d, cc: e.target.value }))} /></label>
          {r.thread ? (
            // A reply in the invoice's own chain keeps its subject; the wording is the only part to write.
            <div style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 10px' }}>
              <b>{draft.subject}</b>
              <div style={{ color: '#15803d', marginTop: 2 }}>↩ Goes as a reply in the same email chain as the invoice sent {new Date(r.thread.first_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{r.thread.count > 1 ? ` (${r.thread.count} emails in it so far)` : ''} — it lands under it in their inbox and in your Sent.</div>
            </div>
          ) : (
            <label><span style={lab}>Subject</span><input style={inp} value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} /></label>
          )}
          <label><span style={lab}>Message</span><textarea style={{ ...inp, resize: 'vertical' }} rows={8} value={draft.body} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} /></label>
          {r.attachment && <label style={{ fontSize: 12, color: '#374151', display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={draft.attach} onChange={e => setDraft(d => ({ ...d, attach: e.target.checked }))} /> Attach the invoice</label>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={sendEmail} disabled={busy || !draft.to.trim()} style={{ ...btn, flex: 1, background: draft.to.trim() ? stage.tint : '#e5e7eb', color: draft.to.trim() ? '#fff' : '#9ca3af' }}>{busy ? 'Sending…' : 'Send it'}</button>
            <button onClick={() => setMode(null)} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#6b7280' }}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'call' && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label><span style={lab}>Who you spoke to and what they said</span><input style={inp} value={note} autoFocus onChange={e => setNote(e.target.value)} placeholder="Left a message for AP… / Check is cut, mailing Friday…" onKeyDown={e => { if (e.key === 'Enter') logCall(); }} /></label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={logCall} disabled={busy} style={{ ...btn, flex: 1, background: stage.tint, color: '#fff' }}>{busy ? 'Saving…' : 'Log the call'}</button>
            <button onClick={() => { setMode(null); setNote(''); }} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#6b7280' }}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'update' && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label><span style={lab}>Daily update on {r.project_number}</span><textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={note} autoFocus onChange={e => setNote(e.target.value)} placeholder="Where this one stands…" /></label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={postUpdate} disabled={busy || !note.trim()} style={{ ...btn, flex: 1, background: note.trim() ? '#1d4ed8' : '#e5e7eb', color: note.trim() ? '#fff' : '#9ca3af' }}>{busy ? 'Posting…' : 'Post update'}</button>
            <button onClick={() => { setMode(null); setNote(''); }} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#6b7280' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// One subject + message per column. Fill-ins are swapped in when the email is drafted.
function WordingEditor({ onClose, onSaved }) {
  const [t, setT] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    apiFetch('/work-orders/followup-templates').then(d => { setT(d.templates); setPlaceholders(d.placeholders || []); setDefaults(d.defaults || {}); }).catch(e => setErr(e?.error || 'Could not load the wording.'));
  }, []);
  async function save() {
    setBusy(true); setErr('');
    try { await apiFetch('/work-orders/followup-templates', { method: 'PUT', body: JSON.stringify({ templates: t }) }); onSaved(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setBusy(false);
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 980, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>✎ Follow-up wording</div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
          One message per column. These fill in when the email is drafted, and can still be edited before it goes: {placeholders.map(p => <code key={p} style={{ background: '#f3f4f6', borderRadius: 4, padding: '1px 5px', marginRight: 4, fontSize: 12 }}>{p}</code>)}
        </div>
        {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{err}</div>}
        {!t ? <div style={{ color: '#9ca3af' }}>Loading…</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14 }}>
            {STAGES.map(s => (
              <div key={s.key} style={{ border: `1px solid ${s.edge}`, background: s.bg, borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: s.tint }}>{s.label}</div>
                  <span style={{ flex: 1 }} />
                  {defaults[s.key] && <button onClick={() => setT(x => ({ ...x, [s.key]: { ...defaults[s.key] } }))} style={{ background: 'none', border: 'none', fontSize: 11.5, color: '#6b7280', cursor: 'pointer', fontWeight: 700 }}>Reset to the standard wording</button>}
                </div>
                <label><span style={lab}>Subject</span><input style={inp} value={t[s.key]?.subject || ''} onChange={e => setT(x => ({ ...x, [s.key]: { ...x[s.key], subject: e.target.value } }))} /></label>
                <label style={{ display: 'block', marginTop: 6 }}><span style={lab}>Message</span><textarea style={{ ...inp, resize: 'vertical' }} rows={9} value={t[s.key]?.body || ''} onChange={e => setT(x => ({ ...x, [s.key]: { ...x[s.key], body: e.target.value } }))} /></label>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#6b7280' }}>Cancel</button>
          <button onClick={save} disabled={busy || !t} style={{ ...btn, background: '#111', color: '#fff' }}>{busy ? 'Saving…' : 'Save wording'}</button>
        </div>
      </div>
    </div>
  );
}
